import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Layout, Menu, Breadcrumb, Button, Space } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  COMPONENT_OUTLINE_BORDER,
  COMPONENT_SPLIT_BORDER_BOTTOM,
} from "./theme/componentBorders";

const { Sider, Content } = Layout;

// 定义菜单项类型
export interface MenuItem {
  key: React.Key;
  icon?: React.ReactNode;
  label: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  onClick?: () => void;
}

// 定义面包屑项类型
export interface BreadcrumbItem {
  title: string;
  href?: string;
}

export type HeaderActionItem = {
  key: string;
  icon: React.ReactNode;
  label?: string;
  onClick?: () => void;
};

// 定义布局配置接口
export interface LayoutConfig {
  // 系统信息
  system: {
    name: string;
    shortName: string;
    logo?: string;
    logoAlt?: string;
  };
  // 菜单配置
  menuItems: MenuItem[];
  // 面包屑配置（可选，如果不提供则自动生成）
  breadcrumbs?: BreadcrumbItem[];
  // 顶部右侧按钮
  headerActions?: HeaderActionItem[];
  /** 标题栏右侧自定义区域（如页面级操作按钮），与 headerActions 并列 */
  headerExtra?: React.ReactNode;
  // 样式配置
  styles?: {
    siderBackground?: string;
    headerBackground?: string;
    contentBackground?: string;
    primaryColor?: string;
  };
}

interface MainLayoutProps {
  config: LayoutConfig;
  children?: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ config, children }) => {
  const {
    system,
    menuItems,
    breadcrumbs: providedBreadcrumbs,
    headerActions,
    headerExtra,
    styles,
  } = config;
  const location = useLocation();
  const navigate = useNavigate();

  // 边栏拖拽相关状态
  const [siderWidth, setSiderWidth] = useState(200);
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(200);

  // 边栏宽度限制
  const MIN_SIDER_WIDTH = 160;
  const MAX_SIDER_WIDTH = 300;

  // 递归查找匹配的菜单项（包括子菜单）
  const findMatchingMenuItem = useCallback(
    (items: MenuItem[], path: string): MenuItem | null => {
      for (const item of items) {
        if (item.path) {
          // 精确匹配
          if (item.path === path) {
            return item;
          }
          // 支持子路径匹配
          if (path.startsWith(item.path + "/")) {
            return item;
          }
        }

        // 递归查找子菜单
        if (item.children && item.children.length > 0) {
          const childMatch = findMatchingMenuItem(item.children, path);
          if (childMatch) {
            return childMatch;
          }
        }
      }
      return null;
    },
    []
  );

  // 根据当前路径获取对应的菜单key（包括子菜单）
  const getSelectedMenuKeys = useMemo(() => {
    const path = location.pathname;
    if (!menuItems || !Array.isArray(menuItems) || menuItems.length === 0) {
      return [];
    }

    const matchingItem = findMatchingMenuItem(menuItems, path);
    if (matchingItem) {
      // 返回匹配项及其所有父项的key
      const keys: string[] = [];
      const findPath = (
        items: MenuItem[],
        target: MenuItem,
        path: string[] = []
      ): string[] | null => {
        for (const item of items) {
          const currentPath = [...path, String(item.key)];
          if (item === target) {
            return currentPath;
          }
          if (item.children) {
            const result = findPath(item.children, target, currentPath);
            if (result) return result;
          }
        }
        return null;
      };
      const keyPath = findPath(menuItems, matchingItem);
      return keyPath || [String(matchingItem.key)];
    }

    // 如果是根路径，返回第一个菜单项的key
    if (path === "/" && menuItems[0]?.key) {
      return [String(menuItems[0].key)];
    }

    return [];
  }, [location.pathname, menuItems, findMatchingMenuItem]);

  // 将菜单项转换为Ant Design Menu组件需要的items格式
  const convertToMenuItems = useCallback((items: MenuItem[]): any[] => {
    if (!items || items.length === 0) {
      return [];
    }

    return items.map((item) => {
      const menuItem: any = {
        key: String(item.key),
        label: item.label,
        icon: item.icon,
      };

      // 递归处理子菜单
      if (item.children && item.children.length > 0) {
        menuItem.children = convertToMenuItems(item.children);
      }

      return menuItem;
    });
  }, []);

  const menuItemsForAntd = useMemo(
    () => convertToMenuItems(menuItems || []),
    [menuItems, convertToMenuItems]
  );

  // 递归查找菜单项（包括子菜单）
  const findMenuItemByKey = useCallback(
    (items: MenuItem[], targetKey: string): MenuItem | null => {
      for (const item of items) {
        if (String(item.key) === String(targetKey)) {
          return item;
        }
        if (item.children && item.children.length > 0) {
          const childMatch = findMenuItemByKey(item.children, targetKey);
          if (childMatch) {
            return childMatch;
          }
        }
      }
      return null;
    },
    []
  );

  // 处理菜单点击事件
  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      if (!menuItems || !Array.isArray(menuItems) || menuItems.length === 0) {
        return;
      }

      const menuItem = findMenuItemByKey(menuItems, key);

      if (menuItem) {
        // 优先使用 onClick
        if (menuItem.onClick) {
          menuItem.onClick();
        } else if (menuItem.path) {
          // 如果没有 onClick，使用 path 进行导航
          try {
            navigate(menuItem.path, { replace: false });
          } catch (error) {
            console.error("[MainLayout] navigate 调用失败:", error);
            window.location.href = menuItem.path;
          }
        }
      }
    },
    [menuItems, findMenuItemByKey, navigate]
  );

  // 自动生成面包屑（如果未提供）
  const breadcrumbs = useMemo(() => {
    if (providedBreadcrumbs && providedBreadcrumbs.length > 0) {
      return providedBreadcrumbs;
    }

    // 自动生成面包屑
    const path = location.pathname;
    const breadcrumbItems: BreadcrumbItem[] = [
      {
        title: "首页",
        href: "/",
      },
    ];

    // 从菜单项中找到当前路径对应的项
    const currentMenuItem = findMatchingMenuItem(menuItems || [], path);
    if (currentMenuItem) {
      breadcrumbItems.push({
        title: String(currentMenuItem.label),
        href: currentMenuItem.path,
      });
    }

    return breadcrumbItems;
  }, [providedBreadcrumbs, location.pathname, menuItems, findMatchingMenuItem]);

  // 处理鼠标按下事件
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      isResizingRef.current = true;
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = siderWidth;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [siderWidth]
  );

  // 处理鼠标移动事件
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;

    e.preventDefault();
    const deltaX = e.clientX - startXRef.current;
    const newWidth = Math.max(
      MIN_SIDER_WIDTH,
      Math.min(MAX_SIDER_WIDTH, startWidthRef.current + deltaX)
    );
    setSiderWidth(newWidth);
  }, []);

  // 处理鼠标释放事件
  const handleMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    setIsResizing(false);

    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  // 添加和移除事件监听器
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // 样式配置
  const siderBackground = styles?.siderBackground || "#f0f1f2";
  const headerBackground = styles?.headerBackground || "#fff";
  const contentBackground = styles?.contentBackground || "#fff";

  return (
    <Layout style={{ height: "100vh", overflow: "hidden" }}>
      {/* 左侧菜单栏 */}
      <Sider
        theme="light"
        style={{
          background: siderBackground,
          borderRight: COMPONENT_OUTLINE_BORDER,
          position: "relative",
          overflow: "hidden",
        }}
        width={siderWidth}
      >
        {/* Logo区域 */}
        <div
          style={{
            width: "100%",
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: COMPONENT_OUTLINE_BORDER,
            background: siderBackground,
          }}
        >
          {system.logo ? (
            <img
              src={system.logo}
              alt={system.logoAlt || system.name}
              style={{ maxHeight: "40px", maxWidth: "80%" }}
            />
          ) : (
            <span
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#1f1f1f",
                textAlign: "center",
              }}
            >
              {system.shortName || system.name}
            </span>
          )}
        </div>

        {/* 主菜单 */}
        {menuItemsForAntd.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
            <div>⚠️ 菜单项为空</div>
          </div>
        ) : (
          <Menu
            theme="light"
            mode="inline"
            style={{
              background: siderBackground,
              border: "none",
              height: "calc(100vh - 64px)",
              overflow: "auto",
            }}
            selectedKeys={getSelectedMenuKeys}
            items={menuItemsForAntd}
            onClick={handleMenuClick}
          />
        )}

        {/* 拖拽手柄 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "4px",
            height: "100%",
            background: isResizing
              ? styles?.primaryColor || "#1890ff"
              : "transparent",
            cursor: "col-resize",
            zIndex: 10,
            transition: "background-color 0.2s",
          }}
          onMouseDown={handleMouseDown}
        />
      </Sider>

      {/* 右侧内容区 */}
      <Layout style={{ height: "100vh", overflow: "hidden" }}>
        {/* 标题栏 */}
        <div
          style={{
            background: headerBackground,
            borderBottom: COMPONENT_SPLIT_BORDER_BOTTOM,
            padding: "0 24px",
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            {breadcrumbs && breadcrumbs.length > 0 && (
              <Breadcrumb
                items={breadcrumbs.map((item) => ({
                  title: item.href ? (
                    <a
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.href!);
                      }}
                      style={{ color: styles?.primaryColor || "#1890ff" }}
                    >
                      {item.title}
                    </a>
                  ) : (
                    item.title
                  ),
                }))}
              />
            )}
          </div>
          {(headerActions && headerActions.length > 0) || headerExtra ? (
            <Space size="middle" style={{ flexShrink: 0 }}>
              {headerActions?.map((a: HeaderActionItem) => (
                <Button
                  key={a.key}
                  type="text"
                  icon={a.icon}
                  onClick={a.onClick}
                >
                  {a.label}
                </Button>
              ))}
              {headerExtra}
            </Space>
          ) : null}
        </div>

        {/* 主内容区域：flex 列，子节点 flex:1 获得明确高度，便于子页 height:100% 生效 */}
        <Content
          style={{
            padding: "16px",
            background: contentBackground,
            height: "calc(100vh - 64px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            {children || <div>欢迎使用 {system.name}</div>}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
